export enum ReportDatasetKind {
  DETAIL = "detail",
  GROUPED = "grouped",
  SUMMARY = "summary",
  PIVOT = "pivot",
}

export enum ReportScope {
  WORKSPACE = "workspace",
  BRANCH = "branch",
  PARISH = "parish",
}

export enum ReportViewType {
  TABLE = "table",
  SUMMARY = "summary",
  CHART = "chart",
  GROUPED_TABLE = "groupedTable",
  PIVOT = "pivot",
  PRINTABLE = "printable",
}

export enum ReportChartType {
  BAR = "bar",
  LINE = "line",
  PIE = "pie",
}

export enum ReportExportType {
  CSV = "csv",
  EXCEL = "excel",
  PDF = "pdf",
}

export enum ReportExecutionClass {
  INTERACTIVE = "interactive",
  DASHBOARD = "dashboard",
  EXPORT = "export",
  ASYNC_EXPORT = "asyncExport",
}

export enum RelationshipUsage {
  COLUMN_SAFE = "columnSafe",
  AGGREGATE_ONLY = "aggregateOnly",
  GROUPABLE = "groupable",
  DRILL_ONLY = "drillOnly",
  FORBIDDEN = "forbidden",
}

export enum RelationshipCardinality {
  ONE_TO_ONE = "oneToOne",
  MANY_TO_ONE = "manyToOne",
  ONE_TO_MANY = "oneToMany",
  MANY_TO_MANY = "manyToMany",
}

export enum ReportValueType {
  TEXT = "text",
  NUMBER = "number",
  CURRENCY = "currency",
  DATE = "date",
  DATETIME = "datetime",
  BOOLEAN = "boolean",
  PERCENTAGE = "percentage",
  SELECT = "select",
}

export enum MeasureAggregation {
  COUNT = "count",
  COUNT_DISTINCT = "countDistinct",
  SUM = "sum",
  AVG = "avg",
  MIN = "min",
  MAX = "max",
  DERIVED = "derived",
  RESOLVER = "resolver",
}

export enum SortDirection {
  ASC = "asc",
  DESC = "desc",
}

export enum ReportAction {
  SAVE = "save",
  DUPLICATE = "duplicate",
  SHARE = "share",
  SCHEDULE = "schedule",
  ADD_TO_DASHBOARD = "addToDashboard",
}

// Reporting shares the canonical filter operator vocabulary (string values preserved, so existing saved
// definitions stay valid). New operators (notContains, isNull, isNotNull, relativeDate) come along for free.
export { FilterOperator as ReportFilterOperator } from "../common/filter-spec";

/** Time bucketing for a date/datetime dimension; compiled via `date_trunc`. */
export enum ReportTimeGrain {
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
  QUARTER = "quarter",
  YEAR = "year",
}

export enum ReportModule {
  REPORTS = "REPORTS",
  MEMBERS = "MEMBERS",
  FINANCE = "FINANCE",
  ACTIVITIES = "ACTIVITIES",
  BUDGETING = "BUDGETING",
  FORMS = "FORMS",
  GROUPS = "GROUPS",
  MINISTRIES = "MINISTRIES",
}

export enum ReportVisibility {
  /** Only the report's creator can see it. */
  PRIVATE = "private",
  /** Shared with everyone who can access the report's branch. */
  BRANCH = "branch",
}

export enum ReportExecutionStatus {
  SUCCEEDED = "succeeded",
  FAILED = "failed",
  CANCELLED = "cancelled",
}

export enum ReportSensitivity {
  NORMAL = "normal",
  SENSITIVE = "sensitive",
  RESTRICTED = "restricted",
}

/**
 * The engine templates offered on the CONSOLIDATED (workspace-scope) report catalog.
 *
 * Lives in shared-types because the boundary has two halves that must not drift: the tenant catalog decides
 * what is OFFERED, and an API test asserts none of these templates selects a person-level dimension. Keeping
 * the list in the frontend meant the backend check had to duplicate it.
 *
 * Two rules decide membership:
 *
 * 1. **No named people, and no branch-internal structure.** Church-wide access is not a licence to read one
 *    branch's members, groups or ministries. Those reports stay on the branch catalog, where the viewer
 *    already has access to the people in them.
 * 2. **It has to mean something consolidated** — either it splits BY BRANCH (which only says anything
 *    church-wide) or it aggregates to a figure that survives being summed across branches.
 *
 * `pledges-without-fulfillment` was on this list and has been removed: it reads as an aggregate from its name,
 * but it groups by `member.fullName` and so lists named members across every branch. Judge these by the
 * dimensions a template selects, never by what its id sounds like.
 */
/**
 * Templates whose ROWS are branches (ADR-0024).
 *
 * A subset of the consolidated list above, singled out because under `BRANCH_AUTONOMY` they are the ones
 * that cannot degrade gracefully. Every other consolidated template aggregates to a figure that simply gets
 * smaller when a branch is withheld; these NAME the branches, one per row, so a withheld branch either
 * disappears from a list that claims to be church-wide or appears with zeros — and a zero row reads as
 * "that branch earned nothing", which is a different and worse statement than "you may not see this".
 *
 * Judged by the dimension the template SELECTS, exactly as the list above is: `income-by-category` stays
 * off it because a category is not a branch, however church-wide the figure is.
 */
export const BRANCH_DIMENSION_REPORT_TEMPLATE_IDS: readonly string[] = [
  "net-balance-by-branch",
  "income-by-branch",
  "expense-by-branch",
  "pledge-fulfillment-by-branch",
  "budget-plan-by-branch"
];

export const CONSOLIDATED_REPORT_TEMPLATE_IDS: readonly string[] = [
  // Money in and out, split by the dimension that only exists church-wide.
  "income-vs-expense-trend",
  "net-balance-by-branch",
  "income-by-branch",
  "expense-by-branch",
  "income-by-category",
  "expense-by-category",
  "income-category-trend",
  // Pledges — aggregate only, never by member, group or ministry.
  "pledge-fulfillment-by-branch",
  "pledge-fulfillment-by-activity",
  "pledges-by-contributor-type",
  "pledges-promised-vs-fulfilled-by-month",
  "outstanding-pledges-by-activity",
  // Planning and activity finance.
  "budget-plan-by-branch",
  "budget-plan-by-activity",
  "budget-plan-by-status",
  "finance-by-activity",
  "activities-by-revenue",
  "activities-by-expense"
];
